/**
 * SearchOperationsService
 *
 * Client-side fuzzy search over the prefetched operations corpus. Mirrors the
 * upstream `DefaultSearchOperationsService` from logic-apps-designer v2 with
 * two additions tailored to Otto's picker UI:
 *
 *   1. Results are returned both flat (matches) and grouped-by-connector so
 *      the picker can render connector sections without re-doing the math.
 *   2. The Fuse index is instantiated once per corpus update; callers should
 *      construct one `DefaultSearchOperationsService` and reuse it across
 *      keystrokes (see `useSearchOperationsClient`).
 */

import Fuse, { type FuseResult, type IFuseOptions } from 'fuse.js';
import type { DiscoveryOperation } from '@/services/operationsService';
import {
  isClientBuiltInConnector,
  isManagedApiConnector,
  isServiceProviderConnector,
  toShortManagedApiId,
} from '@/utils/connections';

/**
 * Connector priority tier used for search ranking. Lower = higher priority.
 * Exported so callers can compare against named values instead of magic
 * numbers (e.g. `group.bestConnectorTier === CONNECTOR_TIER.MANAGED_API`).
 */
export const CONNECTOR_TIER = {
  /** `connectionProviders/...` — curated client / in-app built-ins. */
  CLIENT_BUILTIN: 0,
  /** `/serviceProviders/...` — in-process Standard runtime connectors. */
  SERVICE_PROVIDER: 1,
  /** Anything that doesn't match the other patterns — rare fallback. */
  UNKNOWN: 2,
  /** `/managedApis/...` — externally-hosted Azure connectors. */
  MANAGED_API: 3,
} as const;

/** Picker filter mode — narrow the corpus to triggers, actions, or both. */
export type SearchMode = 'trigger' | 'action' | 'all';

/**
 * Predicate that narrows a corpus to triggers / actions for a given picker
 * mode. Returns `undefined` for `'all'` so callers can pass it straight
 * through to `searchAndGroup`'s optional `additionalFilter`.
 */
export const getOperationModeFilter = (
  mode: SearchMode,
): ((op: DiscoveryOperation) => boolean) | undefined => {
  if (mode === 'trigger') return (op) => !!op.properties?.trigger;
  if (mode === 'action') return (op) => !op.properties?.trigger;
  return undefined;
};

/**
 * A single search match — the operation plus the Fuse score (`0` = exact,
 * `1` = no match). Sort order is documented on
 * {@link DefaultSearchOperationsService.searchOperations}.
 */
export interface SearchOperationResult {
  operation: DiscoveryOperation;
  /** Fuse score, lower is better. */
  score: number;
  /**
   * Connector priority tier used to break near-ties in favor of
   * higher-priority connector families. See {@link getConnectorTier}.
   * Lower = higher priority. Exposed so the picker can group / debug.
   */
  connectorTier: number;
}

/**
 * Group of matches that all belong to the same connector. `bestScore` is the
 * minimum score across operations in the group and drives connector ordering
 * (see {@link DefaultSearchOperationsService.searchAndGroup}).
 */
export interface ConnectorSearchGroup {
  /**
   * Stable per-group key produced by `resolveConnectorGroupKey`. Usually
   * the normalized connector slug (`/managedApis/office365`), but may be
   * a synthetic `displayName:` / `name:` / `opSlug:` / `unknown` key when
   * `api.id` is missing or malformed. Don't pattern-match against this to
   * classify the connector — use `bestConnectorTier` instead.
   */
  connectorId: string;
  /** Connector display name, falling back to the id when absent. */
  connectorDisplayName: string;
  /** Whether the connector is custom (built API), per `properties.isCustomApi`. */
  isCustom: boolean;
  /** Lowest score across operations in the group — drives connector ordering. */
  bestScore: number;
  /**
   * Lowest (best) connector tier observed across the group's matches.
   * Used by the group sort to push higher-priority connector families
   * (client built-ins, service providers) ahead of lower-priority ones
   * (managed APIs) when their `bestScore` values are similar.
   */
  bestConnectorTier: number;
  /**
   * Connector "quality" adjustment used as a sub-tier signal in the sort.
   * Negative = boost (popular canonical Microsoft connector — Outlook,
   * Office 365 Outlook, Gmail, SharePoint, etc.). Positive = penalty
   * (publisher-fork variant whose slug or displayName carries a developer
   * username suffix). Zero = neutral. Applied at {@link TIER_PENALTY}
   * magnitude so it interleaves with the connector-tier ranking. See
   * {@link getConnectorQuality}.
   */
  connectorQuality: number;
  /** Matched operations, already sorted ascending by score (capped by `MAX_MATCHES_PER_GROUP`). */
  matches: SearchOperationResult[];
  /** Total Fuse matches for this connector BEFORE per-group truncation. */
  totalMatches: number;
}

const DEFAULT_LIMIT = 100;
/**
 * Internal Fuse limit used when grouping. Must be high enough that one big
 * connector (e.g. a custom connector with hundreds of operations whose names
 * all share the search keyword) cannot consume every result slot and crowd
 * smaller-but-equally-relevant connectors out of the groups list.
 */
const DEFAULT_GROUP_LIMIT = 2000;
/**
 * Per-connector cap applied after grouping so a single connector with
 * thousands of fuzzy matches doesn't bloat memory / UI. Drill-in views
 * lazy-load the full operations list directly from the backend.
 */
const MAX_MATCHES_PER_GROUP = 25;

/**
 * Additive penalty per tier step. Applied on top of the Fuse score (which
 * is in roughly [0, 0.4] given our `threshold: 0.4`) so a near-tie always
 * favors the higher-priority tier, but a much better match in a lower
 * tier still wins. 0.05 was picked so a 3-tier gap (client → managed)
 * equals 0.15 — enough to demote a perfect managed match below a great
 * (but slightly fuzzy) client one, while not enough to push a poor
 * client-tier match above a perfect managed-tier match.
 */
const TIER_PENALTY = 0.05;

/**
 * Classify an operation's connector into a priority tier used by search
 * ranking. See {@link CONNECTOR_TIER} for the meaning of each level.
 *
 * `isCustomApi` is handled as a *hard* rule by the comparator (custom
 * always after non-custom regardless of score) so it is intentionally
 * not folded into this tier.
 */
export const getConnectorTier = (op: DiscoveryOperation): number => {
  const apiId = op.properties?.api?.id ?? '';
  if (isManagedApiConnector(apiId)) return CONNECTOR_TIER.MANAGED_API;
  if (isServiceProviderConnector(apiId)) return CONNECTOR_TIER.SERVICE_PROVIDER;
  if (isClientBuiltInConnector(apiId)) return CONNECTOR_TIER.CLIENT_BUILTIN;
  return CONNECTOR_TIER.UNKNOWN;
};

/**
 * True when a tier value represents an externally-hosted Azure managed API.
 * False for in-app families (service providers, client built-ins, unknown).
 * Centralizes the managed-vs-in-app distinction shared by the merge bucketing
 * and the picker's "Built-in" badge / Azure drill-in branches.
 */
export const isManagedApiTier = (tier: number): boolean =>
  tier === CONNECTOR_TIER.MANAGED_API;

/**
 * Score adjusted by connector tier AND optional connector-quality sub-tier.
 * Used by both the operation-level and group-level sorts so a near-tie
 * always favors the higher-priority tier — and within a tier, popular
 * canonical Microsoft connectors beat their publisher-fork variants. See
 * {@link getConnectorQuality} for the quality scale.
 */
const tierAdjustedScore = (
  score: number,
  tier: number,
  quality: number = 0,
): number => score + (tier + quality) * TIER_PENALTY;

/**
 * Curated set of canonical Microsoft / Google connector slugs that get a
 * search-rank boost when they appear in results. Restricted to the "users
 * almost always mean this one" connectors so a generic query like
 * "email" or "sharepoint" surfaces the canonical connector ahead of
 * dozens of publisher forks and tangentially-related service providers.
 *
 * Slugs must be lowercase. The lookup checks `api.name` first, then falls
 * back to the slug extracted from `api.id`. Add new entries here when you
 * find a popular connector being out-ranked by a marginal SERVICE_PROVIDER
 * match or by a fork variant — keep the list small and curated rather
 * than reflecting every Microsoft connector.
 *
 * Exported so the corpus-loading pipeline (`useAllOperations`) can use the
 * same list to backfill operations for canonicals the Otto control plane
 * silently drops from its cross-connector `/apiOperations` bulk endpoint.
 * The two concerns share the same opinion of "what's a popular Microsoft
 * connector", so they share the source of truth.
 */
export const POPULAR_CANONICAL_SLUGS: ReadonlySet<string> = new Set([
  // Mail
  'outlook',
  'office365',
  'gmail',
  // Files / collaboration
  'sharepointonline',
  'onedrive',
  'onedriveforbusiness',
  'teams',
  // Productivity
  'planner',
  'excelonlinebusiness',
  'microsoftforms',
  'onenote',
  // Calendars / tasks (commonly searched as "outlook")
  'office365users',
  'office365groups',
]);

/**
 * Lowercased `api.displayName` → canonical `/managedApis/{slug}` mapping
 * for popular Microsoft / Google connectors. Used as a last-resort fallback
 * by `repairAzureOpIdentity` (in `useAllOperations.ts`) when the Otto
 * control plane's `/apiOperations` bulk endpoint returns a canonical op
 * with a stripped `api.id` (e.g. `/automationProjects/{p}/managedApis/`)
 * AND the `/managedApis` connector list has no exact-displayName match
 * to join against — which happens when the tenant has only publisher
 * forks of the canonical (`Outlook.com (jumina)`, `Outlook.com (lnunisha)`,
 * …) and no canonical `Outlook.com` connector entry.
 *
 * Without this fallback, multiple canonical brands (Outlook.com, Gmail,
 * OneDrive, …) end up sharing the same malformed `op.id` of shape
 * `/automationProjects/{p}/managedApis//{opName}` (empty-slug double-slash)
 * and silently collide during merge dedupe — only the last brand's page
 * survives, the others vanish from the search corpus entirely.
 *
 * Keys must be lowercase and trimmed. Values must be slugs that exist in
 * {@link POPULAR_CANONICAL_SLUGS} so downstream quality / tier logic
 * recognizes them. Keep this list curated — it's the mirror image of
 * `POPULAR_CANONICAL_SLUGS`, not an arbitrary alias table.
 */
export const CANONICAL_DISPLAY_NAMES: ReadonlyMap<string, string> = new Map([
  // Mail
  ['outlook.com', 'outlook'],
  ['office 365 outlook', 'office365'],
  ['gmail', 'gmail'],
  // Files / collaboration
  ['sharepoint', 'sharepointonline'],
  ['onedrive', 'onedrive'],
  ['onedrive for business', 'onedriveforbusiness'],
  ['microsoft teams', 'teams'],
  // Productivity
  ['planner', 'planner'],
  ['excel online (business)', 'excelonlinebusiness'],
  ['microsoft forms', 'microsoftforms'],
  ['onenote (business)', 'onenote'],
  // Calendars / tasks
  ['office 365 users', 'office365users'],
  ['office 365 groups', 'office365groups'],
]);

/**
 * Slug-shape regex for a publisher-fork connector: a lowercase canonical
 * stem followed by `-` and a lowercase alphanumeric suffix (developer
 * username or short hash). Examples: `office365-anladwig`, `outlook-mengqiqin`,
 * `sharepoint-yubojia`. Does NOT match well-formed multi-word canonical
 * slugs like `azureblob` (no hyphen) or `microsoft-sql` (where the suffix
 * is a product family, not a username).
 */
const PUBLISHER_FORK_SLUG_RE = /^[a-z][a-z0-9]+-[a-z][a-z0-9]{2,}$/;

/**
 * DisplayName-shape regex for a publisher-fork: trailing parenthetical
 * containing a single lowercase alphanumeric token (developer username).
 * Examples: `Office 365 Outlook (anladwig)`, `Outlook.com (mengqiqin)`.
 * Intentionally case-sensitive so designations like `(Independent Publisher)`,
 * `(V2)`, `(Beta)`, `(Preview)` are NOT classified as forks.
 */
const PUBLISHER_FORK_DISPLAY_RE = /\([a-z][a-z0-9]{2,}\)\s*$/;

/**
 * Detect a publisher-fork connector. Either the slug shape
 * (`stem-username`) or the displayName shape (`Name (username)`) is
 * sufficient — control-plane data occasionally has one without the other
 * when a developer suffixed the slug but not the displayName, or vice
 * versa. Forks copy operations verbatim from their canonical parent, so
 * demoting them in search keeps the picker readable.
 *
 * Exported for unit tests.
 */
export const isPublisherFork = (op: DiscoveryOperation): boolean => {
  const apiName = (op.properties?.api?.name ?? '').trim();
  if (apiName && PUBLISHER_FORK_SLUG_RE.test(apiName.toLowerCase())) return true;
  const displayName = (op.properties?.api?.displayName ?? '').trim();
  if (displayName && PUBLISHER_FORK_DISPLAY_RE.test(displayName)) return true;
  // api.name was stripped during transport — try the slug embedded in api.id.
  const slug = extractManagedApiSlug(op.properties?.api?.id ?? '').toLowerCase();
  if (slug && PUBLISHER_FORK_SLUG_RE.test(slug)) return true;
  return false;
};

/**
 * Detect a popular canonical Microsoft / Google connector that should be
 * promoted in search. See {@link POPULAR_CANONICAL_SLUGS} for the
 * allowlist. Exported for unit tests.
 */
export const isPopularCanonical = (op: DiscoveryOperation): boolean => {
  const apiName = (op.properties?.api?.name ?? '').trim().toLowerCase();
  if (apiName && POPULAR_CANONICAL_SLUGS.has(apiName)) return true;
  const slug = extractManagedApiSlug(op.properties?.api?.id ?? '').toLowerCase();
  return !!slug && POPULAR_CANONICAL_SLUGS.has(slug);
};

/**
 * Connector "quality" adjustment used as a sub-tier signal in the sort.
 *   - `-2`: popular canonical Microsoft connector. The boost (-0.10 with
 *     the default {@link TIER_PENALTY}) cancels the MANAGED_API ↔
 *     SERVICE_PROVIDER gap so a canonical Outlook match competes head-to-
 *     head with a tier-1 SMTP / Azure built-in match instead of losing
 *     to a marginally-relevant one purely on tier.
 *   - `+1`: publisher-fork variant. The penalty (+0.05) is small enough
 *     that a fork still appears in results, but always after the
 *     canonical / non-fork variants of the same tier on identical Fuse
 *     scores.
 *   - `0`: everything else.
 *
 * Exported for unit tests and downstream telemetry.
 */
export const getConnectorQuality = (op: DiscoveryOperation): number => {
  if (isPopularCanonical(op)) return -2;
  if (isPublisherFork(op)) return +1;
  return 0;
};

/**
 * Stable key for in-group dedup. Mirrors the label the picker renders
 * (`discoveryOperationToDefinition` uses `summary || op.name`) so two
 * physically distinct ops that look identical to the user collapse to
 * one row. Falls back to `op.id` so unlabeled ops never merge.
 */
export const operationDisplayKey = (op: DiscoveryOperation): string => {
  const summary = op.properties?.summary?.trim();
  if (summary) return `s:${summary}`;
  const name = op.name?.trim();
  if (name) return `n:${name}`;
  return `i:${op.id}`;
};

/**
 * Collapse `items` to one representative per key, keeping the lowest
 * score on collision. Used to dedupe search matches by displayed label
 * (Fuse scores are ascending: lower = better match).
 */
export const dedupeByLowestScore = <T>(
  items: Iterable<T>,
  keyOf: (item: T) => string,
  scoreOf: (item: T) => number,
): T[] => {
  const byKey = new Map<string, T>();
  for (const item of items) {
    const key = keyOf(item);
    const prev = byKey.get(key);
    if (!prev || scoreOf(item) < scoreOf(prev)) byKey.set(key, item);
  }
  return Array.from(byKey.values());
};

/**
 * Extract the connector slug segment from a `/managedApis/{slug}/...`
 * style id. Returns `''` when the id doesn't contain the segment or has
 * no slug following it.
 */
const extractManagedApiSlug = (id: string | undefined): string => {
  if (!id) return '';
  const match = id.match(/\/managedApis\/([^/]+)/i);
  return match?.[1] ?? '';
};

/**
 * Resolve a stable, distinct group key for an operation's connector. The
 * Otto control plane sometimes returns operations whose `api.id` omits the
 * connector slug (e.g. `/automationProjects/{p}/managedApis/` with a trailing
 * slash) which would collapse unrelated connectors into one bogus group.
 *
 * For those malformed cases `api.name` is unreliable too (it often carries
 * the operation name rather than the connector slug), so we cluster by
 * `api.displayName` — the only field that consistently identifies the
 * connector. The resulting `displayName:` key intentionally is NOT in
 * `/managedApis/{slug}` form so `isManagedApiConnector` returns false and
 * the picker's drill-in renders the in-memory matches via
 * `fallbackOperations` (a backend fetch would 404 without a real slug).
 *
 * Well-formed ids are canonicalized to `/managedApis/{slug}` via
 * `toShortManagedApiId` so the same connector returned under different scope
 * paths (project-scoped `/automationProjects/{p}/managedApis/office365` vs
 * region-scoped `/subscriptions/.../locations/.../managedApis/office365` vs
 * already-canonical `/managedApis/office365`) collapses into one group
 * instead of rendering 2-3 visually-identical "Office 365 Outlook" tiles.
 * The canonical short form still satisfies `isManagedApiConnector` and the
 * drill-in's `connectorId.split('/').pop()` slug extraction.
 */
const resolveConnectorGroupKey = (operation: DiscoveryOperation): string => {
  const api = operation.properties?.api;
  const rawId = api?.id?.trim() ?? '';

  const idIsMalformed = !rawId || /\/managedApis\/?$/i.test(rawId);
  if (!idIsMalformed) {
    // Lowercase before short-form normalization so casing variants
    // (`/managedApis/Office365` vs `/managedApis/office365`) also collapse.
    return toShortManagedApiId(rawId).toLowerCase();
  }

  const displayName = api?.displayName?.trim();
  if (displayName) return `displayName:${displayName.toLowerCase()}`;

  const rawName = api?.name?.trim();
  if (rawName) return `name:${rawName.toLowerCase()}`;

  // Last-resort: try to recover a slug from the operation's own id so at
  // least operations sharing one path cluster together.
  const opIdSlug = extractManagedApiSlug(operation.id);
  if (opIdSlug) return `opSlug:${opIdSlug.toLowerCase()}`;

  return 'unknown';
};

/**
 * Collapse groups that share a normalized `connectorDisplayName` but came
 * from distinct connector ids. The Otto control plane surfaces both real
 * same-displayName-different-slug connectors (e.g. `/managedApis/outlook` +
 * `/managedApis/domoutlook` → both "Outlook.com") and developer-fork
 * variants where the publisher appended a suffix to the slug but forgot to
 * update displayName (e.g. `/managedApis/office365-aadilomar` colliding
 * with `/managedApis/office365` → both "Office 365 Outlook"). Either way
 * the user sees visually-identical tiles; collapsing them here keeps the
 * picker readable. Variants whose displayName carries an explicit publisher
 * suffix (`Office 365 Outlook (anladwig)`) stay distinct on purpose.
 *
 * Mutates `groupsById` in place:
 *   - Buckets are keyed on (isCustom, managed-vs-in-app, normalized
 *     displayName) so connectors from different families never blend even
 *     when their labels collide. Without the managed-vs-in-app axis,
 *     `/managedApis/servicebus` and `/serviceProviders/serviceBus` (both
 *     "Service Bus") would merge into one mislabeled tile.
 *   - Winner = shortest connectorId, alphabetic tiebreak — picks the
 *     canonical slug over publisher-suffixed dev forks and the more
 *     commonly-used alias over the legacy one.
 *   - Matches dedupe by `operation.name` (stable across forks); the
 *     winner's match wins on collision so clicking the tile invokes the
 *     canonical connector.
 *   - `totalMatches` becomes the deduped count, never the sum of forks.
 */
const mergeDuplicateDisplayNames = (
  groupsById: Map<string, ConnectorSearchGroup>,
): void => {
  const bucketKey = (g: ConnectorSearchGroup): string => {
    const custom = g.isCustom ? 'c' : 'b';
    const family = isManagedApiTier(g.bestConnectorTier) ? 'm' : 'i';
    return `${custom}:${family}:${g.connectorDisplayName.trim().toLowerCase()}`;
  };

  const buckets = new Map<string, ConnectorSearchGroup[]>();
  for (const g of groupsById.values()) {
    const key = bucketKey(g);
    const list = buckets.get(key) ?? [];
    list.push(g);
    buckets.set(key, list);
  }

  for (const groups of buckets.values()) {
    if (groups.length < 2) continue;

    groups.sort((a, b) =>
      a.connectorId.length !== b.connectorId.length
        ? a.connectorId.length - b.connectorId.length
        : a.connectorId.localeCompare(b.connectorId),
    );
    const winner = groups[0]!;

    // Insertion order = group iteration order = winner first, so the
    // winner-owned match for any given op.name always wins the dedup.
    const byOpName = new Map<string, SearchOperationResult>();
    let bestScore = Number.POSITIVE_INFINITY;
    let bestTier = Number.POSITIVE_INFINITY;
    // Quality is min-aggregated too: a bucket containing both a canonical
    // (quality -2) and a fork-with-stripped-displayName (quality +1) should
    // carry the canonical's boost since the winner-owned matches dominate.
    let bestQuality = Number.POSITIVE_INFINITY;
    for (const g of groups) {
      if (g.bestScore < bestScore) bestScore = g.bestScore;
      if (g.bestConnectorTier < bestTier) bestTier = g.bestConnectorTier;
      if (g.connectorQuality < bestQuality) bestQuality = g.connectorQuality;
      for (const m of g.matches) {
        const opName = m.operation.name?.trim() || m.operation.id;
        if (!opName) continue;
        if (!byOpName.has(opName)) byOpName.set(opName, m);
      }
    }

    winner.matches = Array.from(byOpName.values())
      .sort((a, b) => a.score - b.score)
      .slice(0, MAX_MATCHES_PER_GROUP);
    winner.totalMatches = byOpName.size;
    winner.bestScore = bestScore;
    winner.bestConnectorTier = bestTier;
    winner.connectorQuality = bestQuality;

    for (const g of groups) {
      if (g !== winner) groupsById.delete(g.connectorId);
    }
  }
};

export class DefaultSearchOperationsService {
  private readonly fuse: Fuse<DiscoveryOperation>;

  constructor(private readonly allOperations: DiscoveryOperation[]) {
    this.fuse = new Fuse(allOperations, DefaultSearchOperationsService.searchOptions());
  }

  /** Number of indexed operations — exposed for telemetry / tests. */
  get size(): number {
    return this.allOperations.length;
  }

  /**
   * Search the corpus and return ungrouped, score-sorted matches. Non-custom
   * connectors are ranked ahead of custom connectors (hard rule); within
   * non-customs, ties are broken by connector tier (see
   * {@link getConnectorTier}) and then by Fuse score ascending. An optional
   * `additionalFilter` runs before sorting and is the standard hook for
   * category / trigger-vs-action gating.
   */
  searchOperations(
    searchTerm: string,
    additionalFilter?: (op: DiscoveryOperation) => boolean,
    limit: number = DEFAULT_LIMIT,
  ): SearchOperationResult[] {
    if (!searchTerm.trim() || this.allOperations.length === 0) return [];

    const results = this.fuse.search(searchTerm, { limit });
    return results
      .filter((r) => (additionalFilter ? additionalFilter(r.item) : true))
      .sort(DefaultSearchOperationsService.compareItems)
      .map((r) => ({
        operation: r.item,
        score: r.score ?? 1,
        connectorTier: getConnectorTier(r.item),
      }));
  }

  /**
   * Search and group the results by connector. Groups are sorted by their
   * `bestScore` (the highest-scoring operation in each group bubbles its
   * connector up). Within each group, matches stay sorted by score ascending.
   */
  searchAndGroup(
    searchTerm: string,
    additionalFilter?: (op: DiscoveryOperation) => boolean,
    limit: number = DEFAULT_GROUP_LIMIT,
  ): ConnectorSearchGroup[] {
    const flat = this.searchOperations(searchTerm, additionalFilter, limit);
    if (flat.length === 0) return [];

    // Bucket flat matches by connector so each group can be built from
    // its own slice in a single pass.
    const matchesByConnector = new Map<string, SearchOperationResult[]>();
    for (const match of flat) {
      const id = resolveConnectorGroupKey(match.operation);
      const bucket = matchesByConnector.get(id);
      if (bucket) bucket.push(match);
      else matchesByConnector.set(id, [match]);
    }

    const groupsById = new Map<string, ConnectorSearchGroup>();
    for (const [connectorId, matches] of matchesByConnector) {
      // Collapse rows that share a displayed label (e.g. four Office 365
      // Outlook ops all summarised "Send an email (V2)") to one entry.
      const deduped = dedupeByLowestScore(
        matches,
        (m) => operationDisplayKey(m.operation),
        (m) => m.score,
      ).sort((a, b) => a.score - b.score);

      // Group-level bests are derived from the full pre-dedup match set so
      // sort/ranking signals don't change when collapsing duplicate labels.
      let bestScore = Infinity;
      let bestConnectorTier = Infinity;
      for (const m of matches) {
        if (m.score < bestScore) bestScore = m.score;
        if (m.connectorTier < bestConnectorTier) bestConnectorTier = m.connectorTier;
      }

      const first = matches[0]!;
      const api = first.operation.properties?.api;
      groupsById.set(connectorId, {
        connectorId,
        connectorDisplayName: api?.displayName ?? api?.name ?? connectorId,
        isCustom: !!first.operation.properties?.isCustomApi,
        bestScore,
        bestConnectorTier,
        connectorQuality: getConnectorQuality(first.operation),
        matches: deduped.slice(0, MAX_MATCHES_PER_GROUP),
        totalMatches: deduped.length,
      });
    }

    // Collapse groups whose distinct connector ids resolve to the same
    // displayName (canonical slug + publisher-fork variants, same-display
    // different-slug aliases). See `mergeDuplicateDisplayNames` docblock.
    mergeDuplicateDisplayNames(groupsById);

    // Hard rule: non-custom groups always before custom groups (preserves
    // the long-standing picker invariant). Within each bucket, sort by
    // tier-adjusted score (now folding in the connector-quality sub-tier
    // so popular Microsoft canonicals beat their publisher forks, and
    // canonical managed-APIs compete head-to-head with marginal service-
    // provider matches); alphabetic tiebreak keeps ordering deterministic.
    return Array.from(groupsById.values()).sort((a, b) => {
      if (a.isCustom !== b.isCustom) return a.isCustom ? 1 : -1;
      const adjA = tierAdjustedScore(a.bestScore, a.bestConnectorTier, a.connectorQuality);
      const adjB = tierAdjustedScore(b.bestScore, b.bestConnectorTier, b.connectorQuality);
      if (adjA !== adjB) return adjA - adjB;
      return a.connectorDisplayName.localeCompare(b.connectorDisplayName);
    });
  }

  /**
   * Sort comparator: non-custom connectors first (hard rule), then
   * ascending by tier-adjusted score so client built-ins beat service
   * providers beat managed APIs when their raw Fuse scores are close.
   * Exposed as a static so tests can verify the ordering invariant
   * without a full Fuse instance.
   */
  static compareItems(
    a: FuseResult<DiscoveryOperation>,
    b: FuseResult<DiscoveryOperation>,
  ): number {
    const isACustom = a.item.properties?.isCustomApi ?? false;
    const isBCustom = b.item.properties?.isCustomApi ?? false;

    if (isACustom !== isBCustom) {
      return isACustom ? 1 : -1;
    }
    const adjA = tierAdjustedScore(
      a.score ?? 1,
      getConnectorTier(a.item),
      getConnectorQuality(a.item),
    );
    const adjB = tierAdjustedScore(
      b.score ?? 1,
      getConnectorTier(b.item),
      getConnectorQuality(b.item),
    );
    if (adjA !== adjB) return adjA < adjB ? -1 : 1;
    return 0;
  }

  /**
   * Fuse options — identical weights to the upstream
   * `DefaultSearchOperationsService` so search relevance matches the
   * Logic Apps Designer experience users may already know.
   */
  static searchOptions(): IFuseOptions<DiscoveryOperation> {
    return {
      includeScore: true,
      threshold: 0.4,
      ignoreLocation: true,
      keys: [
        { name: 'properties.summary', weight: 2.1 },
        {
          name: 'displayName',
          getFn: (op: DiscoveryOperation) => op.properties?.api?.displayName ?? '',
          weight: 2,
        },
        {
          // Canonical connector slug (e.g. "sharepoint",
          // "sharepoint-anchhabr"). Critical for connector-name queries
          // like "sharepoint" to find connectors whose individual operation
          // summaries don't contain the keyword (e.g. "Check in file",
          // "Add attachment"). Without this key, only connectors whose ops
          // literally repeat the connector name in their summary will
          // match, which causes a single chatty connector to dominate.
          name: 'apiName',
          getFn: (op: DiscoveryOperation) => op.properties?.api?.name ?? '',
          weight: 2,
        },
        {
          name: 'description',
          getFn: (op: DiscoveryOperation) => op.properties?.api?.description ?? '',
          weight: 1.9,
        },
      ],
    };
  }
}
